import { type Ref, useCallback, useImperativeHandle, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { MutedText, SmallText } from '@/components/ui/typography';

type EmbedTab = 'url' | 'code';

export interface EmbedSettingsFormHandle {
  getValues: () => { url: string; html: string };
}

interface EmbedSettingsFormProps {
  initialUrl: string;
  initialHtml: string;
  resetKey?: unknown;
  ref?: Ref<EmbedSettingsFormHandle | null>;
}

function inferActiveTab(url: string, html: string): EmbedTab {
  if (html.trim()) return 'code';
  if (url.trim()) return 'url';
  return 'code';
}

function LineNumberedTextarea({
  value,
  onChange,
  rows = 8,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  const gutterRef = useRef<HTMLDivElement>(null);
  const lines = value.split('\n');
  const lineCount = Math.max(lines.length, rows);

  const handleScroll = useCallback((event: React.UIEvent<HTMLTextAreaElement>) => {
    if (gutterRef.current) {
      gutterRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  }, []);

  return (
    <div className="flex overflow-hidden rounded-md border border-input">
      <div
        ref={gutterRef}
        className="shrink-0 select-none overflow-hidden border-r border-input bg-muted/30 px-2 py-2 text-right font-mono text-xs leading-[1.625rem] text-muted-foreground"
        aria-hidden
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={`ln-${i + 1}`}>{i + 1}</div>
        ))}
      </div>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={handleScroll}
        rows={rows}
        fieldSizing="fixed"
        className="resize-none rounded-none border-0 font-mono text-sm shadow-none focus-visible:ring-0"
        placeholder='<iframe src="..."></iframe>'
      />
    </div>
  );
}

export function EmbedSettingsForm({
  initialUrl,
  initialHtml,
  resetKey,
  ref,
}: EmbedSettingsFormProps) {
  const [activeTab, setActiveTab] = useState<EmbedTab>(() =>
    inferActiveTab(initialUrl, initialHtml),
  );
  const [localUrl, setLocalUrl] = useState(initialUrl);
  const [localHtml, setLocalHtml] = useState(initialHtml);
  const prevResetKeyRef = useRef(resetKey);

  if (resetKey !== prevResetKeyRef.current) {
    setActiveTab(inferActiveTab(initialUrl, initialHtml));
    setLocalUrl(initialUrl);
    setLocalHtml(initialHtml);
    prevResetKeyRef.current = resetKey;
  }

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        url: activeTab === 'url' ? localUrl.trim() : '',
        html: activeTab === 'code' ? localHtml : '',
      }),
    }),
    [activeTab, localUrl, localHtml],
  );

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab as EmbedTab);
  }, []);

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      <TabsList variant="default" fullWidth>
        <TabsTrigger value="code">Embed Code</TabsTrigger>
        <TabsTrigger value="url">URL</TabsTrigger>
      </TabsList>

      <TabsContent value="url" className="mt-4">
        <div className="space-y-1.5">
          <SmallText className="font-semibold text-foreground">URL</SmallText>
          <MutedText className="text-xs">Paste a public URL to embed content</MutedText>
          <Input
            type="url"
            value={localUrl}
            onChange={(event) => setLocalUrl(event.target.value)}
            placeholder="https://..."
          />
        </div>
      </TabsContent>

      <TabsContent value="code" className="mt-4">
        <div className="space-y-1.5">
          <SmallText className="font-semibold text-foreground">Embed Code</SmallText>
          <MutedText className="text-xs">Paste iframe or script embed code</MutedText>
          <LineNumberedTextarea value={localHtml} onChange={setLocalHtml} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
